import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaCheckCircle, FaClock, FaCrown, FaMars, FaVenus, FaUserPlus, FaShieldAlt, FaChartBar, FaCog } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const StatCard = ({ icon: Icon, label, value, color, link }) => (
  <Link to={link || '#'} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all hover:-translate-y-1">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-sm">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
      </div>
      <div className={`w-14 h-14 rounded-full flex items-center justify-center ${color} bg-opacity-10`} style={{ backgroundColor: `${color}20` }}>
        <Icon className="text-2xl" style={{ color: color === 'text-[#8B0000]' ? '#8B0000' : color === 'text-green-600' ? '#16a34a' : color === 'text-yellow-600' ? '#ca8a04' : '#7c3aed' }} />
      </div>
    </div>
  </Link>
);

const AdminDashboard = () => {
  const { api } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    verifiedUsers: 0,
    pendingVerifications: 0,
    activeSubscriptions: 0,
    maleUsers: 0,
    femaleUsers: 0,
    recentRegistrations: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/api/admin/dashboard');
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#8B0000] border-t-transparent"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#FFF8F0] pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#8B0000] font-['Playfair_Display']">Admin Dashboard</h1>
            <p className="text-gray-600 mt-1 font-['Noto_Sans_Devanagari']">व्यवस्थापक नियंत्रण पॅनेल</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard icon={FaUsers} label="Total Users" value={stats.totalUsers || 0} color="text-[#8B0000]" link="/admin/users" />
            <StatCard icon={FaCheckCircle} label="Verified Users" value={stats.verifiedUsers || 0} color="text-green-600" />
            <StatCard icon={FaClock} label="Pending Verifications" value={stats.pendingVerifications || 0} color="text-yellow-600" link="/admin/verifications" />
            <StatCard icon={FaCrown} label="Active Subscriptions" value={stats.activeSubscriptions || 0} color="text-purple-600" />
          </div>

          {/* Gender Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-[#8B0000] mb-4">Gender Distribution</h3>
              <div className="flex items-center justify-around">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                    <FaMars className="text-3xl text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-blue-600">{stats.maleUsers || 0}</p>
                  <p className="text-gray-500 text-sm">Male</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-2">
                    <FaVenus className="text-3xl text-pink-600" />
                  </div>
                  <p className="text-2xl font-bold text-pink-600">{stats.femaleUsers || 0}</p>
                  <p className="text-gray-500 text-sm">Female</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-[#8B0000] mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                <Link to="/admin/users" className="flex items-center gap-2 p-3 rounded-lg bg-[#FFF8F0] hover:bg-[#8B0000] hover:text-white transition-colors text-[#8B0000]">
                  <FaUsers /> Manage Users
                </Link>
                <Link to="/admin/verifications" className="flex items-center gap-2 p-3 rounded-lg bg-[#FFF8F0] hover:bg-[#8B0000] hover:text-white transition-colors text-[#8B0000]">
                  <FaShieldAlt /> Verifications
                </Link>
                <Link to="/admin/reports" className="flex items-center gap-2 p-3 rounded-lg bg-[#FFF8F0] hover:bg-[#8B0000] hover:text-white transition-colors text-[#8B0000]">
                  <FaChartBar /> Reports
                </Link>
                <Link to="/admin/settings" className="flex items-center gap-2 p-3 rounded-lg bg-[#FFF8F0] hover:bg-[#8B0000] hover:text-white transition-colors text-[#8B0000]">
                  <FaCog /> Settings
                </Link>
              </div>
            </div>
          </div>

          {/* Recent Registrations */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-[#8B0000] mb-4">Recent Registrations</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-gray-600">Name</th>
                    <th className="text-left py-3 px-4 text-gray-600">Email</th>
                    <th className="text-left py-3 px-4 text-gray-600">City</th>
                    <th className="text-left py-3 px-4 text-gray-600">Gender</th>
                    <th className="text-left py-3 px-4 text-gray-600">Joined</th>
                    <th className="text-left py-3 px-4 text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.recentRegistrations || []).map((user, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-[#FFF8F0]">
                      <td className="py-3 px-4 font-medium">{user.first_name} {user.last_name}</td>
                      <td className="py-3 px-4 text-gray-600">{user.email}</td>
                      <td className="py-3 px-4 text-gray-600">{user.city}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${user.gender === 'male' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                          {user.gender}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600">{new Date(user.created_at).toLocaleDateString('en-IN')}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${user.is_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {user.is_verified ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!stats.recentRegistrations || stats.recentRegistrations.length === 0) && (
                    <tr><td colSpan="6" className="py-8 text-center text-gray-400">No recent registrations</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AdminDashboard;
